// CRM BL: Super Admin › Settings › AI Assist (provedor de IA do Capitão).
// Escolher o provedor preenche o endereço; colar a chave carrega os modelos;
// "Testar conexão" mostra o resultado logo abaixo do botão.
// A chave digitada só vai para o próprio servidor (rotas do Super Admin).

import { createModelPicker } from './modelPicker';

const DEBOUNCE_MS = 450;

const RESULT_CLASSES = {
  success: ['outline-n-teal-6', 'bg-n-teal-2', 'text-n-teal-11'],
  error: ['outline-n-ruby-6', 'bg-n-ruby-2', 'text-n-ruby-11'],
  info: ['outline-n-weak', 'bg-n-alpha-1', 'text-n-slate-11'],
};

const parseJSON = value => {
  try {
    return JSON.parse(value || 'null');
  } catch (error) {
    return null;
  }
};

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

const normalizeBase = url => {
  let base = (url || '').trim().replace(/\/+$/, '');
  base = base.replace(/\/(chat\/completions|embeddings|models)$/, '');
  return /\/v\d+(?:alpha|beta)?\d*(?:\/openai)?$/i.test(base)
    ? base
    : `${base}/v1`;
};

const setResult = (element, type, lines) => {
  Object.values(RESULT_CLASSES)
    .flat()
    .forEach(name => element.classList.remove(name));
  element.classList.add(...RESULT_CLASSES[type]);
  element.replaceChildren(
    ...lines.map((line, index) => {
      const row = document.createElement('div');
      if (index === 0) row.className = 'font-medium';
      else row.className = 'pt-0.5 text-xs opacity-90 break-words';
      row.textContent = line;
      return row;
    })
  );
  element.classList.remove('hidden');
};

const describeError = data => {
  const status = data?.status ? `Erro ${data.status}` : 'Erro';
  return `${status}: ${data?.error || 'o provedor não respondeu como esperado.'}`;
};

const initializeCaptainProviderForm = () => {
  const form = document.querySelector('[data-captain-provider-form]');
  if (!form) return;

  const presets = parseJSON(form.dataset.presets) || [];
  const saved = parseJSON(form.dataset.saved) || {};
  const el = selector => form.querySelector(selector);
  const providerSelect = el('[data-provider-select]');
  const endpointDisplay = el('[data-endpoint-display]');
  const endpointPreview = el('[data-endpoint-preview]');
  const endpointField = el('[data-endpoint-field]');
  const endpointInput = el('[data-endpoint-input]');
  const apiKeyInput = el('[data-api-key-input]');
  const modelInput = el('[data-model-input]');
  const embeddingInput = el('[data-embedding-input]');
  const modelsStatus = el('[data-models-status]');
  const loadButton = el('[data-load-models]');
  const testButton = el('[data-test-connection]');
  const testResult = el('[data-test-result]');
  const embeddingsWarning = el('[data-embeddings-warning]');
  const modelPicker = createModelPicker(modelInput);
  const embeddingPicker = createModelPicker(embeddingInput);

  let editingEndpoint =
    providerSelect.value === 'custom' ||
    endpointInput.dataset.overridden === 'true';
  let debounceTimer = null;
  let lastLoadedSignature = null;
  let loadSequence = 0;

  const preset = () => presets.find(item => item.key === providerSelect.value);

  const currentBase = () => {
    if (editingEndpoint && endpointInput.value.trim())
      return normalizeBase(endpointInput.value);
    return preset()?.base || '';
  };

  // A chave salva só serve para o endereço que já está salvo.
  const canUseSavedKey = () =>
    saved.has_key && normalizeBase(currentBase()) === saved.base;

  const hasUsableKey = () => apiKeyInput.value.trim() || canUseSavedKey();

  const requestBody = extra => ({
    provider: providerSelect.value,
    endpoint: editingEndpoint ? endpointInput.value.trim() : '',
    api_key: apiKeyInput.value.trim(),
    ...extra,
  });

  const post = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({
      error: `o servidor respondeu ${response.status}.`,
    }));
    return { ok: response.ok, data };
  };

  const renderEndpoint = () => {
    const isCustom = providerSelect.value === 'custom';
    if (isCustom) editingEndpoint = true;
    endpointPreview.textContent = preset()?.base || '';
    endpointDisplay.classList.toggle('hidden', editingEndpoint);
    endpointField.classList.toggle('hidden', !editingEndpoint);
    endpointInput.disabled = !editingEndpoint;
    el('[data-endpoint-reset]').classList.toggle('hidden', isCustom);
  };

  const renderEmbeddingsWarning = () => {
    const current = preset();
    if (current && current.embeddings === false) {
      embeddingsWarning.textContent = `${current.name} não tem embeddings de 1536 dimensões: a busca no conhecimento do Capitão fica só por texto (sem busca por significado). Para a busca completa, use OpenAI, OpenRouter ou Google Gemini, ou informe aqui um modelo compatível.`;
      embeddingsWarning.classList.remove('hidden');
    } else {
      embeddingsWarning.classList.add('hidden');
    }
  };

  const renderPlaceholders = () => {
    const current = preset();
    modelPicker.setDefault(current?.model);
    embeddingPicker.setDefault(current?.embedding_model);
  };

  const setStatus = (text, tone = 'muted') => {
    modelsStatus.textContent = text;
    modelsStatus.classList.toggle('text-n-ruby-11', tone === 'error');
    modelsStatus.classList.toggle('text-n-teal-11', tone === 'success');
    modelsStatus.classList.toggle('text-n-slate-11', tone === 'muted');
  };

  const loadModels = async ({ force = false } = {}) => {
    if (!currentBase()) {
      setStatus('Informe o endereço da API.', 'error');
      return;
    }
    if (!hasUsableKey()) {
      setStatus(
        saved.has_key
          ? 'Cole a chave deste provedor (a chave salva é de outro endereço).'
          : 'Cole a chave para carregar os modelos.'
      );
      return;
    }
    const signature = `${currentBase()}|${apiKeyInput.value.trim()}`;
    if (!force && signature === lastLoadedSignature) return;

    loadSequence += 1;
    const sequence = loadSequence;
    loadButton.disabled = true;
    modelPicker.setLoading(true);
    embeddingPicker.setLoading(true);
    setStatus('Carregando modelos…');
    try {
      const { ok, data } = await post(form.dataset.modelsUrl, requestBody());
      if (sequence !== loadSequence) return;
      if (!ok) {
        lastLoadedSignature = null;
        setStatus(describeError(data), 'error');
        return;
      }
      lastLoadedSignature = signature;
      const models = data.models || [];
      const embeddingModels = models.filter(model => model.embedding);
      modelPicker.setModels(models.filter(model => !model.embedding));
      embeddingPicker.setModels(
        embeddingModels.length ? embeddingModels : models
      );
      const ids = models.map(model => model.id);
      const defaults = preset();
      if (!modelInput.value && defaults?.model && ids.includes(defaults.model))
        modelPicker.setValue(defaults.model);
      if (
        !embeddingInput.value &&
        defaults?.embedding_model &&
        ids.includes(defaults.embedding_model)
      )
        embeddingPicker.setValue(defaults.embedding_model);
      const embedText = embeddingModels.length
        ? `, ${embeddingModels.length} de embeddings`
        : '';
      setStatus(
        `${models.length} modelos carregados${embedText}. Abra a lista e digite para buscar.`,
        'success'
      );
    } catch (error) {
      if (sequence === loadSequence)
        setStatus('Não foi possível falar com o servidor.', 'error');
    } finally {
      if (sequence === loadSequence) {
        loadButton.disabled = false;
        modelPicker.setLoading(false);
        embeddingPicker.setLoading(false);
      }
    }
  };

  const scheduleLoad = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => loadModels(), DEBOUNCE_MS);
  };

  const testConnection = async () => {
    if (!hasUsableKey()) {
      setResult(testResult, 'error', [
        'Erro: cole a chave da API antes de testar.',
      ]);
      return;
    }
    if (!modelInput.value.trim()) {
      setResult(testResult, 'error', ['Erro: escolha o modelo de chat.']);
      return;
    }
    testButton.disabled = true;
    setResult(testResult, 'info', ['Testando conexão…']);
    try {
      const { data } = await post(
        form.dataset.testUrl,
        requestBody({
          model: modelInput.value.trim(),
          embedding_model: embeddingInput.value.trim(),
        })
      );
      if (data.error && !data.chat) {
        setResult(testResult, 'error', [describeError(data)]);
        return;
      }
      const lines = [];
      const { chat, embeddings } = data;
      if (chat.ok) {
        lines.push(
          `Conexão ok: ${chat.model} respondeu em ${chat.latency_ms} ms.`
        );
      } else {
        lines.push(`Chat (${chat.model}) falhou. ${describeError(chat)}`);
      }
      if (embeddings) {
        lines.push(
          embeddings.ok
            ? `Embeddings ok: ${embeddings.model}, ${embeddings.dimensions} dimensões, ${embeddings.latency_ms} ms.`
            : `Embeddings (${embeddings.model}) falharam. ${describeError(embeddings)}`
        );
      }
      if (data.ok) lines.push('Salve para usar estas configurações.');
      setResult(testResult, data.ok ? 'success' : 'error', lines);
    } catch (error) {
      setResult(testResult, 'error', [
        'Erro: não foi possível falar com o servidor.',
      ]);
    } finally {
      testButton.disabled = false;
    }
  };

  providerSelect.addEventListener('change', () => {
    editingEndpoint = providerSelect.value === 'custom';
    if (!editingEndpoint) endpointInput.value = '';
    const defaults = preset();
    modelPicker.setModels([]);
    embeddingPicker.setModels([]);
    modelPicker.setValue(defaults?.model || '');
    embeddingPicker.setValue(defaults?.embedding_model || '');
    lastLoadedSignature = null;
    testResult.classList.add('hidden');
    renderEndpoint();
    renderEmbeddingsWarning();
    renderPlaceholders();
    setStatus('');
    if (hasUsableKey()) scheduleLoad();
  });

  el('[data-endpoint-edit]').addEventListener('click', () => {
    editingEndpoint = true;
    endpointInput.value = endpointInput.value || preset()?.base || '';
    renderEndpoint();
    endpointInput.focus();
  });

  el('[data-endpoint-reset]').addEventListener('click', () => {
    editingEndpoint = false;
    endpointInput.value = '';
    lastLoadedSignature = null;
    renderEndpoint();
    if (hasUsableKey()) scheduleLoad();
  });

  endpointInput.addEventListener('change', () => {
    lastLoadedSignature = null;
    if (hasUsableKey()) scheduleLoad();
  });
  apiKeyInput.addEventListener('paste', scheduleLoad);
  apiKeyInput.addEventListener('blur', () => {
    if (apiKeyInput.value.trim()) scheduleLoad();
  });
  loadButton.addEventListener('click', () => loadModels({ force: true }));
  testButton.addEventListener('click', testConnection);

  renderEndpoint();
  renderEmbeddingsWarning();
  renderPlaceholders();
  if (canUseSavedKey()) loadModels();
};

export default initializeCaptainProviderForm;
